/**
 * SegmentCards — New / Active / At-Risk / Churned customer counts.
 *
 * #221 Wave 1: converted from inert Tailwind utilities to the ferro-*
 * design system. This was Johnny's literal "GIANT unstyled SVG"
 * finding: the four segment icons were sized only by inert `w-4 h-4`
 * classes with no width/height attributes, so they rendered at the SVG
 * intrinsic 300×150 default. Icons now carry explicit 16px attributes
 * (AC: bounding box ≤ 20px). The widget owns its card chrome
 * (.ferro-dashboard-widget) so the analytics row reads as two
 * consistent cards next to Revenue.
 */
import { useState, useEffect, useCallback } from 'react';
import type { ClientConfig } from '@ferro/config';
import { adminAnalyticsApi, type CustomerSegments } from '../../lib/api.js';

const SEGMENT_CONFIG = [
  {
    key: 'new' as const,
    label: 'New',
    modifier: 'ferro-segment-card--new',
    icon: (
      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    ),
  },
  {
    key: 'active' as const,
    label: 'Active',
    modifier: 'ferro-segment-card--active',
    icon: (
      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
  },
  {
    key: 'atRisk' as const,
    label: 'At-Risk',
    modifier: 'ferro-segment-card--at-risk',
    icon: (
      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
    ),
  },
  {
    key: 'churned' as const,
    label: 'Churned',
    modifier: 'ferro-segment-card--churned',
    icon: (
      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
      </svg>
    ),
  },
] as const;

type SegmentCardsProps = {
  config: ClientConfig;
};

export function SegmentCards({ config }: SegmentCardsProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const [data, setData] = useState<CustomerSegments | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSegments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const result = await adminAnalyticsApi.getSegments(apiUrl);
    setIsLoading(false);
    if (result.error) {
      // Friendly copy only — never surface raw machine codes (#221 QA).
      // Breadcrumb keeps the raw cause findable in the console.
      console.error('[SegmentCards] segments load failed:', result.error);
      setError("Couldn't load customer segments right now.");
    } else if (result.data) {
      setData(result.data);
    }
  }, [apiUrl]);

  useEffect(() => {
    void fetchSegments();
  }, [fetchSegments]);

  return (
    <article
      className="ferro-dashboard-widget"
      aria-label="Customer segments"
      data-testid="segment-cards-widget"
    >
      <header className="ferro-dashboard-widget__header">
        <h2 className="ferro-section__subtitle">Customer Segments</h2>
        {isLoading && (
          <div
            className="ferro-dashboard-widget__spinner"
            role="status"
            aria-label="Loading customer segments"
          />
        )}
      </header>

      {error && (
        <div
          className="ferro-dashboard-widget__error ferro-dashboard-widget__error--retry"
          role="alert"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void fetchSegments()}
            className="ferro-dashboard-widget__action"
          >
            Retry
          </button>
        </div>
      )}

      {isLoading && !data && (
        <div className="ferro-segment-grid" data-testid="segments-skeleton">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="ferro-skeleton ferro-skeleton--stat" />
          ))}
        </div>
      )}

      {data && (
        <div className="ferro-segment-grid">
          {SEGMENT_CONFIG.map((segment) => (
            <div
              key={segment.key}
              className={`ferro-segment-card ${segment.modifier}`}
            >
              <div className="ferro-segment-card__top">
                <span className="ferro-segment-card__icon">{segment.icon}</span>
                <span className="ferro-segment-card__dot" aria-hidden="true" />
              </div>
              <span className="ferro-segment-card__count">{data[segment.key]}</span>
              <span className="ferro-segment-card__label">{segment.label}</span>
            </div>
          ))}
        </div>
      )}

      {!data && !error && !isLoading && (
        <p className="ferro-centered-note">No segment data yet</p>
      )}
    </article>
  );
}
